import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getBusinessContracts,
  getContractMapping,
  mapContractToTemplate,
  unmapContractFromTemplate,
} from '../../../../utils/services/businessContractMappingAPI';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';
import { DESIGN_AREA_SETTINGS_TAB } from '../../../SidebarMenu/translationValues';

const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

export const useBusinessContractSettings = () => {
  const { t } = useTranslation(NAMESPACES);
  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, 'useBusinessContractSettings');

  const [isDataMappingEnabled, setIsDataMappingEnabled] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Track original state for cancellation
  const [originalState, setOriginalState] = useState({
    isDataMappingEnabled: false,
    selectedContractId: null,
  });
  
  // Track if there was an existing mapping initially
  const [hasExistingMapping, setHasExistingMapping] = useState(false);

  // Fetch contracts list
  const fetchContracts = useCallback(async () => {
    try {
      const response = await getBusinessContracts('', 1, 100);
      if (response?.statusCode === 6008 && response?.data) {
        setContracts(response.data);
      } else {
        message.error(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.key || 'Error loading contracts', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.value || 'Error loading contracts'));
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      message.error(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.key || 'Error loading contracts', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.value || 'Error loading contracts'));
    }
  }, [t]);

  // Fetch current mapping
  const fetchCurrentMapping = useCallback(async () => {
    if (!documentId) return;
    
    try {
      const response = await getContractMapping(documentId);
      if (response?.statusCode === 6016 && response?.data) {
        const mapping = response.data;
        const enabled = !!mapping.businessContractId;
        setIsDataMappingEnabled(enabled);
        setSelectedContractId(enabled ? mapping.businessContractId : null);
        setOriginalState({
          isDataMappingEnabled: enabled,
          selectedContractId: enabled ? mapping.businessContractId : null,
        });
        setHasExistingMapping(true);
      } else if (response?.statusCode === 6005) {
        // No mapping found - this is fine for initial state
        setIsDataMappingEnabled(false);
        setSelectedContractId(null);
        setOriginalState({
          isDataMappingEnabled: false,
          selectedContractId: null,
        });
        setHasExistingMapping(false);
      }
    } catch (error) {
      console.error('Error fetching mapping:', error);
      // Don't show error for 404 (no mapping found)
      if (error?.response?.status !== 404) {
        message.error(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.key || 'Error loading mapping', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.value || 'Error loading mapping'));
      }
    } finally {
      setLoading(false);
    }
  }, [documentId, t]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchContracts(), fetchCurrentMapping()]);
    };
    loadData();
  }, [fetchContracts, fetchCurrentMapping]);

  const toggleDataMapping = useCallback(() => {
    setIsDataMappingEnabled(prev => !prev);
    if (isDataMappingEnabled) {
      // If disabling, also clear selected contract
      setSelectedContractId(null);
    }
  }, [isDataMappingEnabled]);

  const selectContract = useCallback((contractId) => {
    setSelectedContractId(contractId);
    // Auto-enable data mapping when a contract is selected
    if (!isDataMappingEnabled && contractId) {
      setIsDataMappingEnabled(true);
    }
  }, [isDataMappingEnabled]);

  const saveMapping = useCallback(async (overrideEnabled = null, overrideContractId = null) => {
    if (!documentId) {
      message.error(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.key || 'Document ID not found', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.value || 'Document ID not found'));
      return false;
    }

    const enabled = overrideEnabled !== null ? overrideEnabled : isDataMappingEnabled;
    const contractId = overrideContractId !== null ? overrideContractId : selectedContractId;

    setSaving(true);
    try {
      // If data mapping is disabled, unmap the contract
      if (!enabled) {
        const response = await unmapContractFromTemplate(documentId);
        if (response?.statusCode === 6014) {
          message.success(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_UNMAPPED?.key || 'Data mapping disabled', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_UNMAPPED?.value || 'Data mapping disabled'));
          setIsDataMappingEnabled(false);
          setSelectedContractId(null);
          setOriginalState({
            isDataMappingEnabled: false,
            selectedContractId: null,
          });
          // Clear the existing mapping flag
          setHasExistingMapping(false);
          return true;
        } else {
          throw new Error(response?.status || 'Failed to unmap contract');
        }
      }

      // If data mapping is enabled but no contract selected
      if (enabled && !contractId) {
        message.error(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_SELECT_REQUIRED?.key || 'Please select a contract', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_SELECT_REQUIRED?.value || 'Please select a contract'));
        setSaving(false);
        return false;
      }

      // Map the contract
      if (enabled && contractId) {
        // If there's an existing mapping and we're changing the contract, unmap first
        if (hasExistingMapping && originalState.selectedContractId && contractId !== originalState.selectedContractId) {
          const unmapResponse = await unmapContractFromTemplate(documentId);
          if (unmapResponse?.statusCode !== 6014) {
            throw new Error(unmapResponse?.status || 'Failed to unmap existing contract');
          }
        }
        
        // Now map the new contract
        const response = await mapContractToTemplate(contractId, documentId);
        if (response?.statusCode === 6012) {
          message.success(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_MAPPED?.key || 'Contract mapped successfully', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_MAPPED?.value || 'Contract mapped successfully'));
          setIsDataMappingEnabled(true);
          setSelectedContractId(contractId);
          setOriginalState({
            isDataMappingEnabled: true,
            selectedContractId: contractId,
          });
          // Mark that we now have an existing mapping
          setHasExistingMapping(true);
          return true;
        } else if (response?.statusCode === 6006) {
          message.error(t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ALREADY_MAPPED?.key || 'Template already mapped to a contract', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ALREADY_MAPPED?.value || 'Template already mapped to a contract'));
          setSaving(false);
          return false;
        } else {
          throw new Error(response?.status || 'Failed to map contract');
        }
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      const errorMessage = error?.response?.data?.errors?.[0]?.description || 
                          error?.message || 
                          t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.key || 'Error saving mapping', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT_ERROR?.value || 'Error saving mapping');
      message.error(errorMessage);
      return false;
    } finally {
      setSaving(false);
    }
    return false;
  }, [documentId, isDataMappingEnabled, selectedContractId, hasExistingMapping, originalState, t]);

  const cancelChanges = useCallback(() => {
    setIsDataMappingEnabled(originalState.isDataMappingEnabled);
    setSelectedContractId(originalState.selectedContractId);
  }, [originalState]);

  const hasPendingChanges = useCallback(() => {
    return (
      isDataMappingEnabled !== originalState.isDataMappingEnabled ||
      selectedContractId !== originalState.selectedContractId
    );
  }, [isDataMappingEnabled, selectedContractId, originalState]);

  return {
    isDataMappingEnabled,
    selectedContractId,
    contracts,
    loading,
    saving,
    toggleDataMapping,
    selectContract,
    saveMapping,
    cancelChanges,
    hasPendingChanges: hasPendingChanges(),
    hasExistingMapping,
  };
};

